import React, { useEffect, useMemo } from "react";
import { Bus, CheckCircle2, Route, Users } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { MapPanel } from "../components/MapPanel";
import { StatCard } from "../components/StatCard";
import HandoverPanel from "../components/HandoverPanel";
import { formatDateTime, getStatusLabel } from "../lib/utils";

export default function SchoolDashboard() {
  const { overview, trips, childrenRecords, loadOverview, loadTrips, loadChildren, confirmArrival } = useData();

  useEffect(() => {
    const hydrate = async () => {
      await Promise.all([loadOverview(), loadTrips(), loadChildren()]);
    };
    hydrate();
    const interval = window.setInterval(hydrate, 10000);
    return () => window.clearInterval(interval);
  }, [loadOverview, loadTrips, loadChildren]);

  const markers = useMemo(
    () =>
      trips
        .filter((trip) => trip.status === "in_progress" && trip.last_location)
        .map((trip) => ({
          id: trip.id,
          latitude: trip.last_location.latitude,
          longitude: trip.last_location.longitude,
          label: `${trip.child_name} • ${trip.vehicle_registration || "Vehicle"}`,
        })),
    [trips]
  );

  const handoverTrip = useMemo(
    () => trips.find((trip) => trip.status === "in_progress") || trips.find((trip) => trip.status === "completed") || null,
    [trips]
  );


  const handleConfirmArrival = async (tripId) => {
    const result = await confirmArrival(tripId);
    if (result.success) {
      toast.success("Arrival confirmed for school records.");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Pupils linked" value={overview?.stats?.children || childrenRecords.length || 0} icon={Users} accent="blue" helper="Children associated with this school" />
        <StatCard label="Active trips" value={overview?.stats?.active_trips || 0} icon={Route} accent="emerald" helper="Vehicles currently travelling to your setting" />
        <StatCard label="Completed trips" value={overview?.stats?.completed_today || 0} icon={Bus} accent="amber" helper="Journeys already completed" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="guardian-card bg-white p-6">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Approaching vehicles</h2>
          <p className="mt-1 text-sm text-[#64748B]">Monitor live approach status for pupils linked to your school.</p>
          <div className="mt-4">
            <MapPanel markers={markers} height={420} title="School live arrivals" />
          </div>
        </div>

        <div className="guardian-card bg-white p-6">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Pupil roster</h2>
          <div className="mt-4 space-y-3">
            {childrenRecords.length ? (
              childrenRecords.map((child) => (
                <div key={child.id} className="rounded-2xl border border-[#E2E8F0] p-4">
                  <p className="font-semibold text-[#0F172A]">{child.name}</p>
                  <p className="mt-1 text-sm text-[#64748B]">Pickup: {child.pickup_location}</p>
                  <p className="text-sm text-[#64748B]">Dropoff: {child.dropoff_location}</p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No pupils linked yet.</div>
            )}
          </div>
        </div>
      </div>

      {handoverTrip && (
        <HandoverPanel trip={handoverTrip} role="school" onRefresh={loadTrips} />
      )}

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Trips for your pupils</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">Child</th>
                <th className="px-6 py-3 font-medium">Vehicle</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">ETA</th>
                <th className="px-6 py-3 font-medium">Updated</th>
                <th className="px-6 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {trips.length ? (
                trips.map((trip) => (
                  <tr key={trip.id} className="border-t border-[#E2E8F0]">
                    <td className="px-6 py-4 font-medium text-[#0F172A]">{trip.child_name}</td>
                    <td className="px-6 py-4 text-[#475569]">{trip.vehicle_registration || "Pending"}</td>
                    <td className="px-6 py-4 text-[#475569]">{getStatusLabel(trip.status)}</td>
                    <td className="px-6 py-4 text-[#475569]">{trip.eta_minutes ? `${trip.eta_minutes} min` : "Pending"}</td>
                    <td className="px-6 py-4 text-[#475569]">{formatDateTime(trip.last_location?.timestamp || trip.updated_at)}</td>
                    <td className="px-6 py-4">
                      {trip.status === "completed" && !trip.school_arrival_confirmed_at ? (
                        <button
                          onClick={() => handleConfirmArrival(trip.id)}
                          className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"
                        >
                          <CheckCircle2 size={14} />
                          Confirm arrival
                        </button>
                      ) : (
                        <span className="text-xs text-[#64748B]">{trip.school_arrival_confirmed_at ? "Arrival confirmed" : "No action"}</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-[#64748B]">No school trips loaded.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
